import {createContext, useContext, useEffect, useState, useCallback} from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  joinAdminChat: (chatId: string) => void;
  leaveAdminChat: (chatId: string) => void;
  sendTypingStart: (chatId: string, content?: string) => void;
  sendTypingStop: (chatId: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');

  useEffect(() => {
    // Skip if no user (we authenticate via cookie or token)
    if (!user) {
      return;
    }

    setConnectionStatus('connecting');
    
    // Initialize socket connection
    // In production with split-origin deployment (e.g. Cloudflare Pages + VPS),
    // VITE_API_URL points to the backend server. In development, use current origin
    // so WebSocket connects through the Vite dev proxy (same-origin cookies).
    const socketUrl = (import.meta.env.PROD && import.meta.env.VITE_API_URL) || window.location.origin;
    
    console.log('🔌 Connecting to socket server at:', socketUrl);

    const newSocket = io(socketUrl, {
      path: '/socket.io/', // Explicitly set the path
      transports: ['websocket', 'polling'], // Prioritize websocket
      withCredentials: true,
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      forceNew: true,
    });

    newSocket.on('connect', () => {
      console.log('🔌 Connected to socket server, socket id:', newSocket.id);
      console.log('🔌 Transport:', newSocket.io.engine.transport.name);
      setIsConnected(true);
      setConnectionStatus('connected');
    });

    newSocket.on('disconnect', (reason) => {
      console.log('🔌 Disconnected from socket server, reason:', reason);
      setIsConnected(false);
      setConnectionStatus('disconnected');
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
      console.error('❌ Full error:', error);
      // Log additional details if available
      if ((error as any).data) {
        console.error('❌ Error data:', (error as any).data);
      }
      if ((error as any).description) {
        console.error('❌ Error description:', (error as any).description);
      }
      
      setIsConnected(false);
      setConnectionStatus('error');
    });

    newSocket.on('error', (error) => {
      console.error('🚨 Socket error:', error);
      setConnectionStatus('error');
    });

    // Debug: Log all incoming events
    newSocket.onAny((eventName, ...args) => {
      console.log(`📨 Socket event received: ${eventName}`, args);
    });

    // Debug: Log all outgoing events
    newSocket.onAnyOutgoing((eventName, ...args) => {
      console.log(`📤 Socket event sent: ${eventName}`, args);
    });

    setSocket(newSocket);

    return () => {
      console.log('🔌 Cleaning up socket connection');
      newSocket.disconnect();
      setSocket(null);
      setIsConnected(false);
      setConnectionStatus('disconnected');
    };
  }, [user]);

  // Separate effect to handle logout
  useEffect(() => {
    if (!user && socket) {
      console.log('🔌 User logged out, disconnecting socket');
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
      setConnectionStatus('disconnected');
    }
  }, [user, socket]);

  const joinAdminChat = useCallback((chatId: string) => {
    if (socket && isConnected) {
      console.log('[Chat Flow] 🚪 Joining admin chat room:', chatId);
      socket.emit('join-admin-chat', chatId);
    } else {
      console.warn('[Chat Flow] ⚠️ Cannot join chat - socket not connected');
    }
  }, [socket, isConnected]);

  const leaveAdminChat = useCallback((chatId: string) => {
    if (socket && isConnected) {
      console.log('[Chat Flow] 🚪 Leaving admin chat room:', chatId);
      socket.emit('leave-admin-chat', chatId);
    }
  }, [socket, isConnected]);

  const sendTypingStart = useCallback((chatId: string, content?: string) => {
    if (socket && isConnected) {
      // Do not transmit live input text for privacy.
      socket.emit('admin-chat-typing-start', chatId);
    }
  }, [socket, isConnected]);

  const sendTypingStop = useCallback((chatId: string) => {
    if (socket && isConnected) {
      // console.log('[Chat Flow] ⌨️ Sending typing stop:', chatId);
      socket.emit('admin-chat-typing-stop', chatId);
    }
  }, [socket, isConnected]);

  const value: SocketContextType = {
    socket,
    isConnected,
    connectionStatus,
    joinAdminChat,
    leaveAdminChat,
    sendTypingStart,
    sendTypingStop
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
